import {
  Person,
  AttachMoney,
  Notifications,
  PlaylistAddCheck,
  MoreVertOutlined,
  FileDownloadOutlined,
  ReportOutlined,
  CheckCircleOutlineOutlined,
  CancelOutlined,
  RestoreOutlined,
} from '@mui/icons-material';
import { Box, styled, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  ADD_BANK_ACCOUNT,
  ADD_BANK_ACCOUNT_WITHOUT_FEED,
  BankReconciliationTab,
  createImportStatementFileRoute,
} from 'adminConstants/routes';
import api from 'lib/trpcClient';
import { useLayoutContext } from 'contexts/LayoutContext';
import { Column, Row } from 'further-ui/components/Layout';
import { numberToCurrencyString } from 'further-ui/utils';

import BankReconciliationContextProvider, {
  useBankReconciliationContext,
} from 'contexts/BankReconciliationContext';
import useManageBankAccount from 'hooks/lp/data/bankAccount/useManageBankAccount';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import ErrorBoundary from 'components/ErrorBoundary';
import Button from 'components/Button';

import Page from 'components/new/Page';
import BankAccountSelect from 'components/new/BankAccountSelect';

import ImportingTransactions from './ImportingTransactions';
import BankStatement from './BankStatement';
import AccountSettings from './AccountSettings';
import ReconcileTransactions from './ReconcileTransactions';
import ReconciledTransactions from './ReconciledTransactions';
import Menu from 'further-ui/components/Menu';

const tabsData = {
  [BankReconciliationTab.ReconcileTransactions]: {
    icon: <Person />,
    title: 'Reconcile transactions',
  },
  [BankReconciliationTab.BankStatement]: {
    icon: <AttachMoney />,
    title: 'Bank statement',
  },
  [BankReconciliationTab.ReconciledTransactions]: {
    icon: <PlaylistAddCheck />,
    title: 'Reconciled transactions',
  },
  [BankReconciliationTab.AccountSettings]: {
    icon: <Notifications />,
    title: 'Account settings',
  },
};

const Balances = styled(Column)(({ theme }) => ({
  width: '100%',
  maxWidth: '440px',
  alignItems: 'flex-end',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

const Balance = styled(Row)(({ theme }) => ({
  width: '100%',
  border: `1px solid ${theme.palette.grey[300]}`,
  padding: theme.spacing(
    theme.spacingSizes.xs,
    theme.spacingSizes.xl,
    theme.spacingSizes.xs,
    theme.spacingSizes.lg,
  ),
  justifyContent: 'space-between',

  '&:first-of-type': {
    borderBottom: 'none',
  },
}));

const RenewButton = styled(Button)(({ theme }) => ({
  color: theme.palette.common.white,
  borderColor: theme.palette.common.white,
  fontWeight: theme.typography.fontWeightBold,

  '&:hover': {
    color: theme.palette.common.white,
    borderColor: theme.palette.common.white,
  },
}));

const BankReconciliationWrapper: React.FC = () => {
  const { selectedVehicleId } = useLayoutContext();

  const [bankAccounts] = api.bankAccount.listBankAccounts.useSuspenseQuery({
    vehicleId: selectedVehicleId ?? '',
  });

  return (
    <BankReconciliationContextProvider
      vehicleId={selectedVehicleId ?? ''}
      bankAccounts={bankAccounts ?? []}
    >
      <BankReconciliation />
    </BankReconciliationContextProvider>
  );
};

const BankReconciliation: React.FC = () => {
  const { hash } = useLocation();
  const history = useHistory();
  const { selectedBankAccount, setSelectedBankAccountId, bankAccounts } =
    useBankReconciliationContext();
  const [activeTab, setActiveTab] = useState<BankReconciliationTab>(
    (hash as BankReconciliationTab) ||
      BankReconciliationTab.ReconcileTransactions,
  );
  const {
    refreshBankFeed,
    deactivateBankFeed,
    renewBankConnection,
    isRefreshingBankFeed,
    isPending,
  } = useManageBankAccount(selectedBankAccount);
  const [canCreateBankAccount, canImportOperations, canEditBankAccount] =
    useHasPermissions([
      RolePermission.LpCreateBankAccount,
      RolePermission.LpCreateBankAccountOperation,
      RolePermission.LpEditBankAccount,
    ]);

  const handleTabChange = (_: unknown, selectedTab: BankReconciliationTab) => {
    setActiveTab(selectedTab);
  };

  useEffect(() => {
    if (
      !selectedBankAccount ||
      selectedBankAccount.operationsCount > 0 ||
      isRefreshingBankFeed
    )
      return;

    refreshBankFeed();
  }, [selectedBankAccount?.id]);

  const balancesMatch =
    selectedBankAccount?.balanceFromStatement ===
    selectedBankAccount?.calculatedBalance;

  const BalanceIcon = balancesMatch
    ? CheckCircleOutlineOutlined
    : ReportOutlined;
  const balanceIconColor = balancesMatch ? 'primary' : 'warning';
  const balanceTooltipText = balancesMatch
    ? 'Your statement balance and Further-calculated balance match. No action is required at this moment.'
    : 'Your statement balance and Further-calculated balance do not match. This may be because a transaction is missing or duplicated on your bank statement. Please review the system statement against your bank account to identify and fix any discrepancy.';

  const isBankFeedConnectionExpired = !!(
    selectedBankAccount?.bankFeedAccessValidTo &&
    selectedBankAccount.bankFeedAccessValidTo <= new Date()
  );

  const handleBankAccountAddClick = (useFeed: boolean) => {
    if (useFeed) {
      history.push(ADD_BANK_ACCOUNT);
    } else {
      history.push(ADD_BANK_ACCOUNT_WITHOUT_FEED);
    }
  };

  return (
    <Page
      tabTitle="Bank Reconciliation"
      spacing="lg"
      messages={
        isBankFeedConnectionExpired
          ? [
              {
                key: 'bank-feed-connection-expired',
                content: (
                  <Row justify="center" centered spacing="md">
                    <Typography
                      variant="body1"
                      color="text.white"
                      fontWeight="bold"
                    >
                      Your bank feed connection has expired and requires renewal
                    </Typography>
                    <RenewButton
                      variant="outlined"
                      onClick={renewBankConnection}
                    >
                      Renew bank feed
                    </RenewButton>
                  </Row>
                ),
                variant: 'warning',
              },
            ]
          : undefined
      }
    >
      <Column spacing="lg">
        <Row justify="space-between" fullWidth breakpoint="md">
          <BankAccountSelect
            bankAccounts={bankAccounts ?? []}
            onBankAccountChange={setSelectedBankAccountId}
            onBankAccountAdd={handleBankAccountAddClick}
            selectedBankAccountId={selectedBankAccount?.id}
          />
          <Balances spacing="sm">
            <Box sx={{ width: '100%' }}>
              <Balance centered spacing="md">
                <Row centered spacing="xs">
                  <Tooltip title={balanceTooltipText}>
                    <BalanceIcon color={balanceIconColor} />
                  </Tooltip>
                  <Typography variant="body1" color="text.secondary">
                    Latest statement balance
                  </Typography>
                </Row>
                <Typography variant="h6" align="right">
                  {numberToCurrencyString(
                    selectedBankAccount?.balanceFromStatement,
                    {
                      currency: selectedBankAccount?.accountCurrency,
                    },
                  )}
                </Typography>
              </Balance>
              <Balance centered spacing="md">
                <Row centered spacing="xs">
                  <Tooltip title={balanceTooltipText}>
                    <BalanceIcon color={balanceIconColor} />
                  </Tooltip>
                  <Typography variant="body1" color="text.secondary">
                    Further-calculated balance
                  </Typography>
                </Row>
                <Typography variant="h6" align="right">
                  {numberToCurrencyString(
                    selectedBankAccount?.calculatedBalance,
                    {
                      currency: selectedBankAccount?.accountCurrency,
                    },
                  )}
                </Typography>
              </Balance>
            </Box>
            <Menu
              label="Bank account actions"
              items={[
                ...(canCreateBankAccount
                  ? [
                      {
                        label: 'Renew bank connection',
                        icon: RestoreOutlined,
                        onClick: renewBankConnection,
                        disabled:
                          isPending || !selectedBankAccount?.bankFeedAccountId,
                      },
                    ]
                  : []),
                ...(canImportOperations
                  ? [
                      {
                        label: 'Import a statement file',
                        icon: FileDownloadOutlined,
                        onClick: () =>
                          history.push(
                            createImportStatementFileRoute(
                              selectedBankAccount?.id ?? '',
                            ),
                          ),
                        disabled: isPending,
                      },
                    ]
                  : []),
                ...(canEditBankAccount
                  ? [
                      {
                        label: 'Deactivate bank feed',
                        icon: CancelOutlined,
                        onClick: deactivateBankFeed,
                        disabled:
                          isPending || !selectedBankAccount?.bankFeedAccountId,
                        color: 'error' as const,
                      },
                    ]
                  : []),
              ]}
              handleIcon={MoreVertOutlined}
            />
          </Balances>
        </Row>

        <Tabs
          showDivider
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          {Object.keys(tabsData).map((tab) => (
            <Tab
              key={tab}
              value={tab}
              label={
                <Row centered spacing="xs">
                  {tabsData[tab].icon}
                  {tabsData[tab].title}
                </Row>
              }
            />
          ))}
        </Tabs>
        {isRefreshingBankFeed ? (
          <ImportingTransactions />
        ) : selectedBankAccount ? (
          <Box>
            {activeTab === BankReconciliationTab.ReconcileTransactions && (
              <ErrorBoundary>
                <ReconcileTransactions bankAccount={selectedBankAccount} />
              </ErrorBoundary>
            )}
            {activeTab === BankReconciliationTab.BankStatement && (
              <ErrorBoundary>
                <BankStatement bankAccount={selectedBankAccount} />
              </ErrorBoundary>
            )}
            {activeTab === BankReconciliationTab.ReconciledTransactions && (
              <ErrorBoundary>
                <ReconciledTransactions bankAccount={selectedBankAccount} />
              </ErrorBoundary>
            )}
            {activeTab === BankReconciliationTab.AccountSettings && (
              <ErrorBoundary>
                <AccountSettings bankAccount={selectedBankAccount} />
              </ErrorBoundary>
            )}
          </Box>
        ) : null}
      </Column>
    </Page>
  );
};

export default BankReconciliationWrapper;
